import {useGetGroupFiltersQuery} from '../../../../api/services/groupService';
import {useLazyGetStudentFiltersQuery} from '../../../../api/services/studentService';
import FORM_AUDIENCE_TYPE from '../../../../constants/FormAudienceType';
import React, {useEffect, useMemo, useState} from 'react';
import SettingsSectionBase, {SettingsSection} from './SettingsSection';
import {Box, Checkbox, FormControlLabel, Tooltip, Typography} from '@mui/material';
import EmailsInput from '../../../../shared-components/EmailsInput';
import AdmicityTableV2 from '../../../../shared-components/Table/V2/AdmicityTableV2';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import PeopleOutlineOutlinedIcon from '@mui/icons-material/PeopleOutlineOutlined';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import AddStudentGroupDialog from './AddStudentGroupDialog';
import RecipientsPreviewDialog from '../FormCreation/RecipientsPreviewDialog';
import {mergeArraysByKey, mergeArraysByKeys} from '../../../../utility/mergeArraysByKey';
import {validateAccessLevel, validateGroupFilters} from '../../../../utility/validationUtil';
import {GROUP_MEMBERS_TYPE} from '../../../../constants/GroupMembersType';
import PaginatedAutocomplete from '../../../../shared-components/PaginatedAutocomplete';
import {useGetCurrentSchoolQuery} from '../../../../api/services/schoolManagementService';
import {useLazyGetAllEmployeeProfilesQuery} from '../../../../api/services/employeeService';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import AssignmentIndOutlinedIcon from '@mui/icons-material/AssignmentIndOutlined';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import AdmicityStatus from '../../../../shared-components/AdmicityStatus';
import WarningIcon from '@mui/icons-material/Warning';
import FORM_ASSIGNMENT_TYPE from '../../../../constants/FormAssignmentType';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import EmailIcon from '@mui/icons-material/Email';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {PARENT_AUDIENCE_OPTION} from '../../../../constants/ParentAudienceType';
import {
    LINKED_EMPLOYEE_SUBMISSION_LABEL,
    LINKED_EMPLOYEE_SUBMISSION_TOOLTIP,
} from '../linkedEmployeeSubmissionMode';

/* eslint-disable react/prop-types */

const options = {
    [FORM_ASSIGNMENT_TYPE.students]: {
        id: FORM_ASSIGNMENT_TYPE.students,
        label: 'Student Recipients',
        icon: <PeopleOutlineOutlinedIcon fontSize="small"/>,
    },
    [FORM_ASSIGNMENT_TYPE.employees]: {
        id: FORM_ASSIGNMENT_TYPE.employees,
        label: 'Employee Recipients',
        icon: <AssignmentIndOutlinedIcon fontSize="small"/>,
    },
};

const getGroupFilterStatusInfo = (isExcluded) =>
    ({
        label: isExcluded ? 'Excluded' : 'Included',
        color: isExcluded ? 'red' : 'green'
    });

const getRecipientSortLabel = (item = {}) =>
    item.label ||
    item.displayName ||
    item.name ||
    `${item.preferredForename ?? ''} ${item.preferredSurname ?? ''}`.trim();

const sortRecipientFilters = (filters = []) => {
    const activeFilters = filters
        .filter(filter => !filter.isDeleted && !filter.deleted)
        .sort((a, b) => getRecipientSortLabel(a).localeCompare(getRecipientSortLabel(b), undefined, {sensitivity: 'base'}));
    const deletedFilters = filters.filter(filter => filter.isDeleted || filter.deleted);

    return [...activeFilters, ...deletedFilters];
};

const RegularFormSettings = (
    {
        formId = null,
        audience,
        parentAudienceOption = null,
        settings,
        originalSettings: originalSettingsProp,
        strictRecipientsFiltering,
        isFormLive,
        isFormArchived,
        accessLevel = null,
        onSettingsChange,
        onSetStrictRecipientsFiltering,
        isCommunicationForm = false,
        linkToStudents,
        linkToEmployees,
        allLinkedEmployeesCanSubmit,
        onAllLinkedEmployeesCanSubmitChange,
        sendToPriority1ContactsOnly = false,
        onRollOnly = false,
    }) => {
    const {
        groupFilters,
        studentFilters,
        applicationFilters,
        enquiryFilters,
        employeeFilters,
        externalUserEmailAssignments,
        assignmentType
    } = settings;
    const originalSettings = {
        groupFilters: originalSettingsProp?.groupFilters ?? [],
        studentFilters: originalSettingsProp?.studentFilters ?? [],
        applicationFilters: originalSettingsProp?.applicationFilters ?? [],
        enquiryFilters: originalSettingsProp?.enquiryFilters ?? [],
        employeeFilters: originalSettingsProp?.employeeFilters ?? [],
        externalUserEmailAssignments: originalSettingsProp?.externalUserEmailAssignments ?? [],
        assignmentType: originalSettingsProp?.assignmentType ?? FORM_ASSIGNMENT_TYPE.none,
        linkedGroupFilters: originalSettingsProp?.linkedGroupFilters ?? [],
        linkedStudentFilters: originalSettingsProp?.linkedStudentFilters ?? [],
        linkedApplicationFilters: originalSettingsProp?.linkedApplicationFilters ?? [],
        linkedEnquiryFilters: originalSettingsProp?.linkedEnquiryFilters ?? [],
        linkedEmployeeFilters: originalSettingsProp?.linkedEmployeeFilters ?? [],
        linkedStrictFiltering: !!originalSettingsProp?.linkedStrictFiltering,
    };

    const canLinkToStudents =
        !isCommunicationForm &&
        audience === FORM_AUDIENCE_TYPE.employees &&
        assignmentType === FORM_ASSIGNMENT_TYPE.employees;
    const canLinkToEmployees = canLinkToStudents;

    const shouldFetchSchoolInfo =
        !!audience && (audience !== FORM_AUDIENCE_TYPE.employees || (canLinkToStudents && linkToStudents === true));

    const {data: schoolInfo} = useGetCurrentSchoolQuery({}, {skip: !shouldFetchSchoolInfo});
    const {data} = useGetGroupFiltersQuery({}, {skip: !audience});
    const handleChangeGroupFilters = filters =>
        onSettingsChange(prev => ({
            ...prev,
            groupFilters: filters,
            validation: {
                ...validateGroupFilters(filters),
                ...validateAccessLevel(accessLevel, filters)
            }
        }));

    const handleStudentFiltersChange = value =>
        onSettingsChange(prev => ({
            ...prev,
            ...Object.fromEntries(
                Object.entries(value).map(([key, filters]) => [key, sortRecipientFilters(filters)])
            )
        }));

    const handleEmployeeFiltersChange = filters =>
        onSettingsChange(prev => ({
            ...prev,
            employeeFilters: sortRecipientFilters(filters)
        }));

    const handleChangeLinkedGroupFilters = (filters) =>
        onSettingsChange(prev => ({
            ...prev,
            linkedGroupFilters: filters
        }));

    const handleLinkedStudentFiltersChange = (value) =>
        onSettingsChange(prev => ({
            ...prev,
            ...Object.fromEntries(
                Object.entries(value).map(([key, filters]) => [key, sortRecipientFilters(filters)])
            )
        }));

    const handleLinkedEmployeeFiltersChange = (filters) =>
        onSettingsChange(prev => ({
            ...prev,
            linkedEmployeeFilters: sortRecipientFilters(filters)
        }));

    const handleSetLinkedStrictFiltering = (checked) =>
        onSettingsChange(prev => ({
            ...prev,
            linkedStrictFiltering: checked
        }));

    const handleAssignmentTypeChange = (e) => {
        const newType = e.target.value;

        onSettingsChange((prev) => {
            const nextFilters = {
                studentFilters: mergeArraysByKey([], originalSettings.studentFilters),
                applicationFilters: mergeArraysByKey([], originalSettings.applicationFilters),
                enquiryFilters: mergeArraysByKey([], originalSettings.enquiryFilters),
                employeeFilters: mergeArraysByKey([], originalSettings.employeeFilters),
                groupFilters: mergeArraysByKeys([], originalSettings.groupFilters, ['id', 'excluded']),
                linkedGroupFilters: [],
                linkedStudentFilters: [],
                linkedApplicationFilters: [],
                linkedEnquiryFilters: [],
                linkedEmployeeFilters: [],
                linkedStrictFiltering: false
            };

            return {
                ...prev,
                assignmentType: newType,
                ...nextFilters
            };
        });
    };

    return (
        <Box display="flex" flexDirection="column" gap={2}>
            {
                audience === FORM_AUDIENCE_TYPE.externalUsers && (
                    <>
                        <SettingsSectionBase
                            header="External User Recipients"
                            headerIcon={<PublicOutlinedIcon fontSize="small"/>}
                            subHeader="Add email addresses for recipients without an account or linked profile."
                        >
                            <SettingsSection>
                                <EmailsInput
                                    disabled={isFormArchived}
                                    values={externalUserEmailAssignments.filter(x => !x.isDeleted)}
                                    onChange={emails => {
                                        if (isFormArchived) return;
                                        onSettingsChange(prev => ({
                                            ...prev,
                                            externalUserEmailAssignments: mergeArraysByKey(
                                                emails,
                                                originalSettings.externalUserEmailAssignments,
                                                'email')
                                        }));
                                    }}
                                    onBlur={email => {
                                        if (isFormArchived) return;
                                        onSettingsChange(prev => ({
                                            ...prev,
                                            externalUserEmailAssignments: mergeArraysByKey(
                                                [...settings.externalUserEmailAssignments, {email}],
                                                originalSettings.externalUserEmailAssignments,
                                                'email')
                                        }));
                                    }}
                                />
                            </SettingsSection>
                        </SettingsSectionBase>
                        <SettingsSectionBase
                            header="Select Recipient Type"
                            headerIcon={<EmailIcon fontSize="small"/>}
                            subHeader="Choose the recipient group type."
                        >
                            <SettingsSection>
                                <FormControl
                                    variant="outlined"
                                    disabled={
                                        isFormArchived ||
                                        (settings && ([
                                            ...settings.groupFilters,
                                            ...settings?.employeeFilters,
                                            ...settings?.studentFilters,
                                            ...settings?.applicationFilters,
                                            ...settings?.enquiryFilters,
                                        ].filter(x => x.isDeleted == null || !x.isDeleted).length > 0))
                                    }
                                >
                                    <InputLabel id="assignment-type-label">Assignment Type</InputLabel>
                                    <Select
                                        labelId="assignment-type-label"
                                        value={assignmentType}
                                        onChange={handleAssignmentTypeChange}
                                        label="Assignment Type"
                                        renderValue={(selected) => {
                                            const {
                                                icon,
                                                label
                                            } = options[selected];
                                            return (
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    {icon}
                                                    {label}
                                                </Box>
                                            );
                                        }}
                                        variant={'outlined'}
                                    >
                                        {Object.entries(options).map(([key, {
                                            id,
                                            label,
                                            icon
                                        }]) =>
                                            (
                                                <MenuItem key={key} value={id}>
                                                    <Box display="flex" alignItems="center" gap={1}>
                                                        {icon}
                                                        {label}
                                                    </Box>
                                                </MenuItem>
                                            ))}
                                    </Select>
                                </FormControl>
                            </SettingsSection>
                        </SettingsSectionBase>
                    </>
                )}

            {assignmentType === FORM_ASSIGNMENT_TYPE.employees &&
                (audience === FORM_AUDIENCE_TYPE.employees || audience === FORM_AUDIENCE_TYPE.externalUsers) && (
                    <EmployeeFiltersSelector
                        formId={formId}
                        originalSettings={originalSettings}
                        audience={audience}
                        recipientType={assignmentType}
                        accessLevel={accessLevel}
                        externalEmails={externalUserEmailAssignments}
                        groupFilterModules={data?.modules}
                        groupFilters={groupFilters}
                        employeeFilters={employeeFilters}
                        strictRecipientsFiltering={strictRecipientsFiltering}
                        isFormLive={isFormLive}
                        isFormArchived={isFormArchived}
                        linkToStudents={linkToStudents}
                        linkToEmployees={linkToEmployees}
                        allLinkedEmployeesCanSubmit={allLinkedEmployeesCanSubmit}
                        onChangeGroupFilters={handleChangeGroupFilters}
                        onSetStrictRecipientsFiltering={onSetStrictRecipientsFiltering}
                        onAllLinkedEmployeesCanSubmitChange={onAllLinkedEmployeesCanSubmitChange}
                        onSettingsChange={onSettingsChange}
                        onEmployeeFiltersChange={handleEmployeeFiltersChange}
                    />
                )}

            {canLinkToStudents &&
                linkToStudents === true && (
                    <LinkedStudentAssociationSelector
                        formId={formId}
                        originalSettings={originalSettings}
                        groupFilterModules={data?.modules || []}
                        linkedGroupFilters={settings.linkedGroupFilters || []}
                        linkedStudentFilters={settings.linkedStudentFilters || []}
                        linkedApplicationFilters={settings.linkedApplicationFilters || []}
                        linkedEnquiryFilters={settings.linkedEnquiryFilters || []}
                        linkedStrictFiltering={!!settings.linkedStrictFiltering}
                        isFormLive={isFormLive}
                        isFormArchived={isFormArchived}
                        allLinkedEmployeesCanSubmit={allLinkedEmployeesCanSubmit}
                        onChangeLinkedGroupFilters={handleChangeLinkedGroupFilters}
                        onSetLinkedStrictFiltering={handleSetLinkedStrictFiltering}
                        onAllLinkedEmployeesCanSubmitChange={onAllLinkedEmployeesCanSubmitChange}
                        onLinkedStudentFiltersChange={handleLinkedStudentFiltersChange}
                        schoolInfo={schoolInfo}
                    />
                )}

            {canLinkToEmployees &&
                linkToEmployees === true && (
                    <LinkedEmployeeAssociationSelector
                        formId={formId}
                        originalSettings={originalSettings}
                        groupFilterModules={data?.modules || []}
                        linkedGroupFilters={settings.linkedGroupFilters || []}
                        linkedEmployeeFilters={settings.linkedEmployeeFilters || []}
                        linkedStrictFiltering={!!settings.linkedStrictFiltering}
                        isFormLive={isFormLive}
                        isFormArchived={isFormArchived}
                        allLinkedEmployeesCanSubmit={allLinkedEmployeesCanSubmit}
                        onChangeLinkedGroupFilters={handleChangeLinkedGroupFilters}
                        onSetLinkedStrictFiltering={handleSetLinkedStrictFiltering}
                        onAllLinkedEmployeesCanSubmitChange={onAllLinkedEmployeesCanSubmitChange}
                        onLinkedEmployeeFiltersChange={handleLinkedEmployeeFiltersChange}
                    />
                )}

            {assignmentType === FORM_ASSIGNMENT_TYPE.students &&
                (audience === FORM_AUDIENCE_TYPE.students ||
                    audience === FORM_AUDIENCE_TYPE.parentsForChildCompletion ||
                    audience === FORM_AUDIENCE_TYPE.enquiries ||
                    audience === FORM_AUDIENCE_TYPE.previousSchool ||
                    audience === FORM_AUDIENCE_TYPE.externalUsers) && (
                    <StudentFiltersSelector
                        formId={formId}
                        originalSettings={originalSettings}
                        audience={audience}
                        parentAudienceOption={parentAudienceOption}
                        recipientType={assignmentType}
                        accessLevel={accessLevel}
                        externalEmails={externalUserEmailAssignments}
                        groupFilterModules={data?.modules
                            .filter(m => m.membersType !== GROUP_MEMBERS_TYPE.employee)
                            .map(({
                                      filters,
                                      ...rest
                                  }) => ({
                                filters: filters.filter(filter => filter.membersType !== GROUP_MEMBERS_TYPE.employee),
                                ...rest
                            })) || []}
                        groupFilters={groupFilters}
                        studentFilters={studentFilters}
                        applicationFilters={applicationFilters}
                        enquiryFilters={enquiryFilters}
                        strictRecipientsFiltering={strictRecipientsFiltering}
                        isFormLive={isFormLive}
                        isFormArchived={isFormArchived}
                        onChangeGroupFilters={handleChangeGroupFilters}
                        onSetStrictRecipientsFiltering={onSetStrictRecipientsFiltering}
                        onStudentFiltersChange={handleStudentFiltersChange}
                        sendToPriority1ContactsOnly={sendToPriority1ContactsOnly}
                        onRollOnly={onRollOnly}
                    />
                )}
        </Box>
    );
};

function LinkedStudentAssociationSelector(
    {
        formId = null,
        originalSettings,
        groupFilterModules,
        linkedGroupFilters,
        linkedStudentFilters,
        linkedApplicationFilters,
        linkedEnquiryFilters,
        linkedStrictFiltering,
        isFormLive,
        isFormArchived,
        onChangeLinkedGroupFilters,
        onSetLinkedStrictFiltering,
        onLinkedStudentFiltersChange,
        schoolInfo
    }) {
    const studentGroupModules = useMemo(() => {
        const modules = groupFilterModules || [];

        return modules
            .map(({
                      filters,
                      ...rest
                  }) => ({
                ...rest,
                filters: (filters || []).filter(f => f.membersType !== GROUP_MEMBERS_TYPE.employee)
            }))
            .filter(m => (m.filters || []).length > 0);
    }, [groupFilterModules]);

    return (
        <SettingsSectionBase
            header="Student linking"
            headerIcon={<PeopleAltOutlinedIcon fontSize="small"/>}
            subHeader="Select student groups and individual students to link this employee form to. This does not assign the form to parents."
        >
            <GroupFiltersTable
                formId={formId}
                groupFilters={linkedGroupFilters}
                groupFilterModules={studentGroupModules}
                originalSettings={{
                    ...originalSettings,
                    groupFilters: originalSettings.linkedGroupFilters || []
                }}
                strictRecipientsFiltering={linkedStrictFiltering}
                onChangeGroupFilters={onChangeLinkedGroupFilters}
                onChangeStrictRecipientsFiltering={onSetLinkedStrictFiltering}
                isFormLive={isFormLive}
                isFormArchived={isFormArchived}
                header="Link students or applicants using filters and groups"
                audience={FORM_AUDIENCE_TYPE.parentsForChildCompletion}
                hideAssigneeDetails
            />

            <SettingsSection>
                <Typography variant="subtitle1" fontWeight="bold" sx={{mt: 2}}>
                    Link individual students or applicants
                </Typography>

                <PaginatedAutocomplete
                    isSelectionDisabled={isFormArchived}
                    textFieldProps={{
                        label: 'Applications',
                        placeholder: 'Enter name, surname, or full name',
                    }}
                    fetchFn={useLazyGetStudentFiltersQuery}
                    fetchFnProps={{queryParams: {pupilType: 'applicant'}}}
                    getOptionLabel={(option) => option.label}
                    value={linkedApplicationFilters.filter(x => !x.isDeleted)}
                    onChange={(e, newValue) =>
                        onLinkedStudentFiltersChange({
                            linkedApplicationFilters: mergeArraysByKey(
                                newValue,
                                originalSettings.linkedApplicationFilters || []
                            )
                        })
                    }
                />

                <PaginatedAutocomplete
                    isSelectionDisabled={isFormArchived}
                    textFieldProps={{
                        label: 'Students',
                        placeholder: 'Enter name, surname, or full name',
                    }}
                    fetchFn={useLazyGetStudentFiltersQuery}
                    fetchFnProps={{queryParams: {pupilType: 'student'}}}
                    getOptionLabel={(option) => option.label}
                    value={linkedStudentFilters.filter(x => !x.isDeleted)}
                    onChange={(e, newValue) =>
                        onLinkedStudentFiltersChange({
                            linkedStudentFilters: mergeArraysByKey(
                                newValue,
                                originalSettings.linkedStudentFilters || []
                            )
                        })
                    }
                />

                {schoolInfo == null || schoolInfo.disableEnquiriesSync
                    ? null
                    : (
                        <PaginatedAutocomplete
                            isSelectionDisabled={isFormArchived}
                            textFieldProps={{
                                label: 'Enquiries',
                                placeholder: 'Enter name, surname, or full name',
                            }}
                            fetchFn={useLazyGetStudentFiltersQuery}
                            fetchFnProps={{queryParams: {pupilType: 'enquiry'}}}
                            getOptionLabel={(option) => option.label}
                            value={linkedEnquiryFilters.filter(x => !x.isDeleted)}
                            onChange={(e, newValue) =>
                                onLinkedStudentFiltersChange({
                                    linkedEnquiryFilters: mergeArraysByKey(
                                        newValue,
                                        originalSettings.linkedEnquiryFilters || []
                                    )
                                })
                            }
                        />
                    )}
            </SettingsSection>
        </SettingsSectionBase>
    );
}

function LinkedEmployeeAssociationSelector(
    {
        formId = null,
        originalSettings,
        groupFilterModules,
        linkedGroupFilters,
        linkedEmployeeFilters,
        linkedStrictFiltering,
        isFormLive,
        isFormArchived,
        onChangeLinkedGroupFilters,
        onSetLinkedStrictFiltering,
        onLinkedEmployeeFiltersChange,
    }) {
    const employeeGroupModules = useMemo(() => {
        const modules = groupFilterModules || [];

        return modules
            .filter(m =>
                m.name === 'EMPLOYEES' ||
                m.name?.toLowerCase().includes('school group') ||
                m.name?.toLowerCase().includes('my group'))
            .map(({
                      filters,
                      ...rest
                  }) => ({
                ...rest,
                filters: (filters || []).filter(f => f.membersType === GROUP_MEMBERS_TYPE.employee)
            }));
    }, [groupFilterModules]);

    return (
        <SettingsSectionBase
            header="Employee linking"
            headerIcon={<PeopleAltOutlinedIcon fontSize="small"/>}
            subHeader="Select employee groups and individual employees to link this employee form to."
        >
            <GroupFiltersTable
                formId={formId}
                groupFilters={linkedGroupFilters}
                groupFilterModules={employeeGroupModules}
                originalSettings={{
                    ...originalSettings,
                    groupFilters: originalSettings.linkedGroupFilters || []
                }}
                strictRecipientsFiltering={linkedStrictFiltering}
                onChangeGroupFilters={onChangeLinkedGroupFilters}
                onChangeStrictRecipientsFiltering={onSetLinkedStrictFiltering}
                isFormLive={isFormLive}
                isFormArchived={isFormArchived}
                header="Link employees using filters and groups"
                audience={FORM_AUDIENCE_TYPE.employees}
                hideAssigneeDetails
            />

            <SettingsSection>
                <Typography variant="subtitle1" fontWeight="bold" sx={{mt: 2}}>
                    Link individual employees
                </Typography>
                <PaginatedAutocomplete
                    isSelectionDisabled={isFormArchived}
                    textFieldProps={{
                        label: 'Employees',
                        placeholder: 'Enter name, surname, or full name',
                    }}
                    fetchFn={useLazyGetAllEmployeeProfilesQuery}
                    getOptionLabel={(option) => option.label || option.preferredForename + ' ' + option.preferredSurname}
                    value={(linkedEmployeeFilters || []).filter(x => !x.isDeleted)}
                    onChange={(e, newValue) =>
                        onLinkedEmployeeFiltersChange(
                            mergeArraysByKey(newValue, originalSettings.linkedEmployeeFilters || [])
                        )
                    }
                />
            </SettingsSection>
        </SettingsSectionBase>
    );
}

function StudentFiltersSelector(
    {
        formId = null,
        originalSettings,
        audience,
        parentAudienceOption = null,
        recipientType = FORM_ASSIGNMENT_TYPE.none,
        accessLevel = null,
        externalEmails = [],
        groupFilterModules,
        groupFilters,
        studentFilters,
        applicationFilters,
        enquiryFilters,
        strictRecipientsFiltering,
        isFormLive,
        isFormArchived,
        onChangeGroupFilters,
        onSetStrictRecipientsFiltering,
        onStudentFiltersChange,
        sendToPriority1ContactsOnly = false,
        onRollOnly = false
    }) {
    const isEnquiryAudience = audience === FORM_AUDIENCE_TYPE.enquiries;
    const isStudentAudience = audience === FORM_AUDIENCE_TYPE.students;
    const isParentAudience = audience === FORM_AUDIENCE_TYPE.parentsForChildCompletion;
    const isParentApplicantStatusAudience =
        isParentAudience && parentAudienceOption === PARENT_AUDIENCE_OPTION.applicantStatus;
    // AD-2581: a form from a template for children on roll only offers on-roll children only.
    const shouldShowApplicantIndividualField =
        !isEnquiryAudience &&
        !isStudentAudience &&
        !onRollOnly;
    const shouldShowStudentIndividualField =
        !isEnquiryAudience &&
        !isParentApplicantStatusAudience;
    const filteredGroupFilterModules = useMemo(() => {
        const modules = groupFilterModules || [];
        const blockedStudentAudienceGroups = ['PRE-ADMISSION', 'FUTURE-ADMISSION', 'ENQUIRY'];
        const applicantOnlyBlockedGroups = ['ON-ROLL', 'FUTURE-ADMISSION', 'ENQUIRY'];

        return modules.filter((group) => {
            const groupName = (group.name || '').toUpperCase();
            const isEnquiryGroup = groupName.includes('ENQUIRY');

            if (isEnquiryAudience) {
                return isEnquiryGroup;
            }

            if (isStudentAudience || onRollOnly) {
                return !blockedStudentAudienceGroups.includes(groupName);
            }

            if (isParentApplicantStatusAudience) {
                return !applicantOnlyBlockedGroups.includes(groupName);
            }

            return !isEnquiryGroup;
        });
    }, [
        groupFilterModules,
        isEnquiryAudience,
        isParentApplicantStatusAudience,
        isStudentAudience,
        onRollOnly
    ]);

    return (
        <SettingsSectionBase
            header="Recipients"
            headerIcon={<PeopleAltOutlinedIcon fontSize="small"/>}
            subHeader={
                isEnquiryAudience
                    ? 'Select enquiry groups and individual enquiries to assign this form to.'
                    : isStudentAudience
                        ? 'Select student groups and individual students to assign this form to.'
                        : isParentApplicantStatusAudience
                            ? 'Select student groups and individual applicants to assign this form to.'
                            : 'Select student groups and individual students or applicants to assign this form to.'
            }
        >
            <GroupFiltersTable
                formId={formId}
                groupFilters={groupFilters}
                groupFilterModules={filteredGroupFilterModules}
                originalSettings={originalSettings}
                strictRecipientsFiltering={strictRecipientsFiltering}
                onChangeGroupFilters={onChangeGroupFilters}
                onChangeStrictRecipientsFiltering={onSetStrictRecipientsFiltering}
                isFormLive={isFormLive}
                isFormArchived={isFormArchived}
                header={
                    isEnquiryAudience
                        ? 'Add enquiries using filters and groups'
                        : isStudentAudience
                            ? 'Add students using filters and groups'
                            : 'Add students or applicants using filters and groups'
                }
                audience={audience}
                recipientType={recipientType}
                accessLevel={accessLevel}
                externalEmails={externalEmails}
                sendToPriority1ContactsOnly={sendToPriority1ContactsOnly}
            />
            <SettingsSection sx={{mt: 2}}>
                <Typography variant="subtitle1" fontWeight="bold">
                    {isEnquiryAudience
                        ? 'Add enquiries individually'
                        : isStudentAudience
                            ? 'Add students individually'
                        : isParentApplicantStatusAudience
                                ? 'Add applicants individually'
                                : 'Add students or applicants individually'}
                </Typography>
                {shouldShowApplicantIndividualField && (
                    <PaginatedAutocomplete
                        isSelectionDisabled={isFormArchived}
                        textFieldProps={{
                            label: 'Applications',
                            placeholder: 'Enter name, surname, or full name',
                            required: true,
                        }}
                        fetchFn={useLazyGetStudentFiltersQuery}
                        fetchFnProps={{
                            queryParams: {
                                pupilType: 'applicant'
                            }
                        }}
                        getOptionLabel={(option) => option.label}
                        value={applicationFilters.filter(filter => !filter.isDeleted)}
                        onChange={(e, newValue) => onStudentFiltersChange({
                            applicationFilters: mergeArraysByKey(newValue, originalSettings.applicationFilters)
                        })}
                    />
                )}
                {shouldShowStudentIndividualField && (
                    <PaginatedAutocomplete
                        isSelectionDisabled={isFormArchived}
                        textFieldProps={{
                            label: 'Students',
                            placeholder: 'Enter name, surname, or full name',
                            required: true,
                            disabled: isFormArchived,
                        }}
                        fetchFn={useLazyGetStudentFiltersQuery}
                        fetchFnProps={{
                            queryParams: {
                                pupilType: 'student'
                            }
                        }}
                        getOptionLabel={(option) => option.label}
                        value={studentFilters.filter(filter => !filter.isDeleted)}
                        onChange={(e, newValue) => onStudentFiltersChange({
                            studentFilters: mergeArraysByKey(newValue, originalSettings.studentFilters)
                        })}
                    />
                )}
                {isEnquiryAudience && (
                    <PaginatedAutocomplete
                        isSelectionDisabled={isFormArchived}
                        textFieldProps={{
                            label: 'Enquiries',
                            placeholder: 'Enter name, surname, or full name',
                            required: true,
                            disabled: isFormArchived,
                        }}
                        fetchFn={useLazyGetStudentFiltersQuery}
                        fetchFnProps={{
                            queryParams: {
                                pupilType: 'enquiry'
                            }
                        }}
                        getOptionLabel={(option) => option.label}
                        value={enquiryFilters.filter(filter => !filter.isDeleted)}
                        onChange={(e, newValue) => onStudentFiltersChange({
                            enquiryFilters: mergeArraysByKey(newValue, originalSettings.enquiryFilters)
                        })}
                    />
                )}
            </SettingsSection>
        </SettingsSectionBase>
    );
}

function EmployeeFiltersSelector(
    {
        formId = null,
        originalSettings,
        audience,
        recipientType = FORM_ASSIGNMENT_TYPE.none,
        accessLevel = null,
        externalEmails = [],
        groupFilterModules,
        groupFilters,
        employeeFilters,
        strictRecipientsFiltering,
        isFormLive,
        isFormArchived,
        linkToStudents,
        linkToEmployees,
        allLinkedEmployeesCanSubmit,
        onChangeGroupFilters,
        onSetStrictRecipientsFiltering,
        onAllLinkedEmployeesCanSubmitChange,
        onEmployeeFiltersChange
    }) {
    const filteredGroupFilterModules = useMemo(() => groupFilterModules
            ?.filter((group) =>
                group.name === 'EMPLOYEES' ||
                group.name?.toLowerCase().includes('school group') ||
                group.name?.toLowerCase().includes('my group'))
            .map(({
                      filters,
                      ...rest
                  }) => ({
                filters: filters.filter(filter => filter.membersType === GROUP_MEMBERS_TYPE.employee),
                ...rest
            })) || [],
        [groupFilterModules?.length]);

    const showLinkedSubmissionMode = !!linkToStudents || !!linkToEmployees;

    return (
        <SettingsSectionBase
            header="Recipients"
            headerIcon={<AssignmentIndOutlinedIcon fontSize="small"/>}
            subHeader="Select employee groups and individual employees to assign this form to."
        >
            <GroupFiltersTable
                formId={formId}
                groupFilters={groupFilters}
                groupFilterModules={filteredGroupFilterModules}
                originalSettings={originalSettings}
                strictRecipientsFiltering={strictRecipientsFiltering}
                onChangeStrictRecipientsFiltering={onSetStrictRecipientsFiltering}
                onChangeGroupFilters={onChangeGroupFilters}
                header="Add employees using filters and groups"
                isFormLive={isFormLive}
                isFormArchived={isFormArchived}
                audience={audience}
                recipientType={recipientType}
                accessLevel={accessLevel}
                externalEmails={externalEmails}
            />
            <SettingsSection>
                <Typography variant="subtitle1" fontWeight="bold" sx={{mt: 2}}>Add individual employee to
                    form</Typography>
                <PaginatedAutocomplete
                    isSelectionDisabled={isFormArchived}
                    textFieldProps={{
                        label: 'Employees',
                        placeholder: 'Enter name, surname, or full name',
                        required: true,
                    }}
                    fetchFn={useLazyGetAllEmployeeProfilesQuery}
                    getOptionLabel={(option) => option.label || option.preferredForename + ' ' + option.preferredSurname}
                    value={employeeFilters.filter(filter => !filter.isDeleted)}
                    onChange={(e, newValue) => {
                        onEmployeeFiltersChange(mergeArraysByKey(newValue, originalSettings.employeeFilters));
                    }}
                />
                {showLinkedSubmissionMode && (
                    <FormControlLabel
                        sx={{
                            gap: 1,
                            ml: 0,
                            mt: 1,
                        }}
                        control={
                            <Checkbox
                                sx={{
                                    padding: 0,
                                }}
                                checked={!!allLinkedEmployeesCanSubmit}
                                disabled={isFormLive || isFormArchived}
                                onChange={(e) => {
                                    onAllLinkedEmployeesCanSubmitChange?.(e.target.checked);
                                }}
                            />
                        }
                        label={
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5
                                }}
                            >
                                {LINKED_EMPLOYEE_SUBMISSION_LABEL}
                                <Tooltip
                                    title={LINKED_EMPLOYEE_SUBMISSION_TOOLTIP}
                                    placement="right"
                                >
                                    <InfoOutlinedIcon fontSize="small" sx={{color: 'text.secondary'}}/>
                                </Tooltip>
                            </Box>
                        }
                    />
                )}
            </SettingsSection>
        </SettingsSectionBase>
    );
}

function GroupFiltersTable(
    {
        formId = null,
        originalSettings,
        groupFilters,
        groupFilterModules,
        onChangeGroupFilters,
        onChangeStrictRecipientsFiltering,
        isFormLive,
        isFormArchived,
        strictRecipientsFiltering,
        hideStrictRecipientsFiltering = false,
        header,
        audience,
        recipientType = FORM_ASSIGNMENT_TYPE.none,
        accessLevel = null,
        externalEmails = [],
        hideAssigneeDetails = false,
        sendToPriority1ContactsOnly = false
    }) {
    const [openAddGroupDialog, setOpenAddGroupDialog] = useState(false);
    const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
    const data = groupFilters.filter(filter => !filter.isDeleted);
    const activeGroupCount = data.filter(filter => !filter.deleted).length;

    useEffect(() => {
        if (hideStrictRecipientsFiltering && strictRecipientsFiltering) {
            onChangeStrictRecipientsFiltering(false);
            return;
        }

        if (activeGroupCount > 1 || !strictRecipientsFiltering) return;
        onChangeStrictRecipientsFiltering(false);
    }, [activeGroupCount, hideStrictRecipientsFiltering, onChangeStrictRecipientsFiltering, strictRecipientsFiltering]);

    return (
        <>
            <SettingsSection>
                    <Typography variant="subtitle1" fontWeight="bold">
                        {header}
                    </Typography>
                    <Box sx={{display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 2}}>
                    <Button
                        sx={{
                            maxWidth: 160,
                        }}
                        variant="outlined"
                        startIcon={<AddOutlinedIcon/>}
                        disabled={isFormArchived}
                        onClick={() => {
                            if (isFormArchived) return;
                            setOpenAddGroupDialog(true);
                        }}
                    >
                        Add Group
                    </Button>
                    <Button
                        variant="contained"
                        sx={{
                            minWidth: 120,
                            boxShadow: 'none',
                            '&:hover': {
                                boxShadow: 'none',
                            },
                        }}
                        disabled={isFormArchived || data.length === 0}
                        onClick={() => setPreviewDialogOpen(true)}
                    >
                        Preview
                    </Button>
                    </Box>
            </SettingsSection>

            {data.some(filter => filter.deleted) && (
                <SettingsSection>
                    <Box
                        display="flex"
                        alignItems="flex-start"
                        gap={1.5}
                        sx={{
                            p: 2.5,
                            backgroundColor: 'rgba(255, 152, 0, 0.08)',
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'rgba(255, 152, 0, 0.3)',
                            mb: 1
                        }}
                    >
                        <WarningIcon
                            sx={{
                                color: 'warning.main',
                                fontSize: 20,
                                mt: 0.25,
                                flexShrink: 0
                            }}
                        />
                        <Typography
                            variant="body2"
                            sx={{
                                color: 'text.primary',
                                lineHeight: 1.5,
                                fontSize: '0.875rem'
                            }}
                        >
                            Some of the groups used in the filter configuration are deactivated. Please review the form
                            filters to ensure the setup is still valid and remove deactivated groups from the filters.
                            New form requests will not be sent for this form until the deactivated groups are removed
                            from the filters.
                        </Typography>
                    </Box>
                </SettingsSection>
            )}

            <SettingsSection>
                <AdmicityTableV2
                    emptyStateMinHeight={80}
                    noRowsOverlay={{text: 'No groups'}}
                    columns={[
                        {
                            accessorKey: 'displayName',
                            header: 'Group Name',
                            grow: true,
                            Cell: ({
                                       cell,
                                       row
                                   }) => {
                                const isDeleted = row.original.deleted;
                                const content = (
                                    <Typography
                                        sx={{
                                            color: isDeleted ? 'error.main' : 'inherit',
                                            textDecoration: isDeleted ? 'line-through' : 'none',
                                            opacity: isDeleted ? 0.7 : 1
                                        }}
                                    >
                                        {cell.getValue()}
                                    </Typography>
                                );

                                return isDeleted
                                    ? (
                                        <Tooltip
                                            title="This group has been deactivated. Please remove it from the filters to ensure the setup is still valid. New form requests will not be sent for this form until the deactivated groups are removed from the filters."
                                            arrow
                                        >
                                            <Box sx={{cursor: 'help'}}>
                                                {content}
                                            </Box>
                                        </Tooltip>
                                    )
                                    : content;
                            }
                        },
                        {
                            accessorKey: 'excluded',
                            header: 'Status',
                            size: 100,
                            grow: false,
                            Cell: ({cell}) => (
                                <AdmicityStatus {...getGroupFilterStatusInfo(cell.getValue())}/>
                            )
                        }
                    ]}
                    data={groupFilters.filter(filter => !filter.isDeleted)}
                    tableProps={{
                        layoutMode: 'grid',
                        enableRowActions: !isFormArchived,
                        enableSorting: false,
                        enableFilters: false,
                        enableHiding: false,
                        enableFullScreenToggle: false,
                        enableColumnResizing: false,
                        enableBottomToolbar: false,
                        enableTopToolbar: false,
                        enablePagination: false,
                        enableStickyHeader: true,
                        enableStickyFooter: false,
                        displayColumnDefOptions: {
                            'mrt-row-actions': {
                                size: 50,
                                grow: false,
                            },
                        },
                        muiTableBodyProps: {
                            sx: {
                                '& :last-child td': {
                                    borderBottom: 0,
                                },
                            }
                        },
                        muiTablePaperProps: {
                            variant: 'outlined',
                            elevation: 0,
                            sx: {
                                display: 'block',
                                flexDirection: 'unset',
                                flexGrow: 0,
                                width: '100%',
                                height: 'auto',
                            }
                        },
                        muiTableContainerProps: {
                            sx: {
                                flex: 'unset',
                                maxHeight: 350,
                                minHeight: 'auto',
                                border: 'none',
                                overflow: 'auto',
                            }
                        },
                        renderRowActions: !isFormArchived
                            ? ({row}) => (
                                <IconButton
                                    onClick={() => {
                                        const filters = groupFilters.filter(filter => row.original.id !== filter.id);
                                        const newFilters = mergeArraysByKeys(
                                            filters,
                                            originalSettings.groupFilters,
                                            ['id', 'excluded']
                                        );

                                        onChangeGroupFilters(newFilters);
                                    }}
                                >
                                    <DeleteOutlineRoundedIcon fontSize="small"/>
                                </IconButton>
                            )
                            : undefined
                    }}
                />
            </SettingsSection>

            {!hideStrictRecipientsFiltering && activeGroupCount > 1 && (
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 1,
                    mt: 2,
                    mb: 1
                }}>
                    <FormControlLabel
                        sx={{gap: 1}}
                        control={
                            <Checkbox
                                sx={{
                                    padding: 0,
                                    marginLeft: 1
                                }}
                                checked={strictRecipientsFiltering}
                                onChange={async (e) => {
                                    if (isFormArchived) return;
                                    await onChangeStrictRecipientsFiltering(e.target.checked);
                                }}
                                disabled={isFormLive || isFormArchived}
                            />
                        }
                        label={
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5
                            }}>
                                Must appear in all these groups
                                <Tooltip
                                    title="When ticked, recipients must belong to every 'Included' group listed above to receive the form. When unticked, they will receive it if they appear in any of the 'Included' groups."
                                    placement="right"
                                >
                                    <InfoOutlinedIcon
                                        fontSize="small"
                                        sx={{
                                            color: 'text.secondary',
                                            cursor: 'pointer'
                                        }}
                                    />
                                </Tooltip>
                            </Box>
                        }
                    />
                </Box>
            )}

            <AddStudentGroupDialog
                open={openAddGroupDialog && !isFormArchived}
                groupFilters={groupFilterModules}
                onClose={() => setOpenAddGroupDialog(false)}
                onChange={(filter => {
                    if (isFormArchived) return;

                    const newFilter = {
                        id: filter.filterId,
                        displayName: filter.paths.map(x => x.name).join(' - '),
                        excluded: filter.excluded
                    };

                    const filters = [...groupFilters];
                    const filterIndex = filters.findIndex(x => x.id === newFilter.id);

                    if (filterIndex >= 0) {
                        filters[filterIndex] = newFilter;
                    } else {
                        filters.push(newFilter);
                    }

                    const newFilters = mergeArraysByKeys(filters, originalSettings.groupFilters, ['id', 'excluded']);

                    onChangeGroupFilters(newFilters);
                })}
            />
            <RecipientsPreviewDialog
                open={previewDialogOpen}
                onClose={() => setPreviewDialogOpen(false)}
                formId={formId}
                groupFilters={data}
                strictRecipientsFiltering={strictRecipientsFiltering}
                audience={audience}
                accessLevel={accessLevel}
                recipientType={recipientType}
                externalEmails={externalEmails}
                hideAssigneeDetails={hideAssigneeDetails}
                sendToPriority1ContactsOnly={sendToPriority1ContactsOnly}
            />
        </>
    );
}

export default RegularFormSettings;
